import { gql } from "@apollo/client";
import { ButtonBase } from "@mui/material";
import { PageContentStack } from "atoms/Stack";
import Stack from "atoms/Stack";
import { MAX_CONTENT_WIDTH } from "helpers/breakpoints";
import { ONSEN_ENV, ONSEN_PUBLIC_LEAGUES_HOST_SUFFIX } from "helpers/environment";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { gaSetSharedEventParams } from "helpers/gtag";
import { SPACING, SPACING_DENSE } from "helpers/spacings";
import useData from "helpers/useData";
import useEnforceAuth from "helpers/useEnforceAuth";
import useRouteState from "helpers/useRouteState";
import { ALL_TEAMS, PlayerListDropdown, TeamListDropdown } from "molecules/PlayerList";
import React, { useEffect, useMemo, useRef, useState } from "react";
import NotFoundPage from "shared/NotFoundPage";
import PageTitle from "shared/PageTitle";
import UnauthorisedPage from "shared/UnauthorisedPage";

import BoxScore, { BoxScoreFragment } from "./games/BoxScore";
import { GameOverviewAiStatsFragment } from "./games/GameOverviewAiStats";
import GameOverviewHighlights from "./games/GameOverviewHighlights";
import GameOverviewPageHeader, { GameOverviewPageHeaderFragment } from "./games/GameOverviewPageHeader";
import GameOverviewReels, {
  GameOverviewReelsFragment,
  GameOverviewReelsGamePlayerFragment,
} from "./games/GameOverviewReels";
import UpdateStubbedUserEmailModal from "./games/GameOverviewUpdateStubbedUserEmailModal";
import PointChart from "./games/PointChart";
import ShotChart from "./games/ShotChart";

const GameOverviewPageGamePlayerFragment = gql`
  fragment GameOverviewPageGamePlayerFragment on GamePlayer {
    id
    playerNumber
    team {
      id
    }
    ...GameOverviewReelsGamePlayerFragment
  }
  ${GameOverviewReelsGamePlayerFragment}
`;

export default function GameOverviewPage({ gameId }) {
  useEnforceAuth({ enforceAuthenticated: true });
  const [gamePlayerId, gamePlayerIdSet] = useRouteState("gamePlayerId");
  const [teamIdParam, teamIdSet] = useRouteState("teamId");
  const [tab] = useRouteState("tab");
  const [stubbedModalOpen, setStubbedModalOpen] = useState(false);
  const isPublicLeaguePage = useMemo(() => {
    if (typeof window === "undefined" || !ONSEN_PUBLIC_LEAGUES_HOST_SUFFIX) {
      return false;
    }

    let locationHost = window.location.host;
    if (ONSEN_ENV === "test") {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const stubHost = urlSearchParams.get("_stub_location_host");
      if (stubHost) {
        locationHost = stubHost;
      }
    }

    return locationHost.endsWith(ONSEN_PUBLIC_LEAGUES_HOST_SUFFIX);
  }, []);

  const [currentUserData, { loading: loadingUser }] = useData(gql`
    query GameOverviewPage_current_user {
      currentUser {
        id
        email
        stubbed
      }
    }
  `);

  const [permissionData, { loading: loadingPermission }] = useData(
    gql`
      query GameOverviewPage_permission($gameId: ID!) {
        game(id: $gameId) {
          id
          canOnsenAccessVideo
        }
      }
    `,
    { gameId },
  );

  const [data, { loading: loadingGame }] = useData(
    gql`
      query GameOverviewPage($gameId: ID!) {
        game(id: $gameId) {
          id
          org {
            gaId
          }
          statsVisible
          gameVideoStatus
          ...GameOverviewPageHeaderFragment
          ...GameOverviewReelsFragment
          ...BoxScoreFragment
          ...GameOverviewAiStatsFragment
        }
      }
      ${GameOverviewPageHeaderFragment}
      ${GameOverviewReelsFragment}
      ${BoxScoreFragment}
      ${GameOverviewAiStatsFragment}
    `,
    { gameId },
    { skip: permissionData && !permissionData.game?.canOnsenAccessVideo },
  );

  const [dataDefaultGamePlayer, { loading: loadingDefaultPlayer }] = useData(
    gql`
      query GameOverviewPage_defaultGamePlayer($gameId: ID!) {
        game(id: $gameId) {
          id
          currentUserDefaultGamePlayer {
            id
            ...GameOverviewPageGamePlayerFragment
          }
          currentUserDefaultTeam {
            id
          }
        }
      }
      ${GameOverviewPageGamePlayerFragment}
    `,
    { gameId },
    { skip: !!gamePlayerId || !!teamIdParam },
  );

  const [dataGamePlayer, { loading: loadingPlayer }] = useData(
    gql`
      query GameOverviewPage_gamePlayer($gamePlayerId: ID!) {
        gamePlayer(id: $gamePlayerId) {
          id
          ...GameOverviewPageGamePlayerFragment
        }
      }
      ${GameOverviewPageGamePlayerFragment}
    `,
    { gamePlayerId },
    { skip: !gamePlayerId },
  );

  const [shotChartAvailableData] = useData(
    gql`
      query GameOverviewPage_shotChartAvailable($gameId: ID!) {
        game(id: $gameId) {
          id
          participations {
            id
            gamePlayers {
              id
              eventsAny(hasCoordinates: true)
            }
          }
        }
      }
    `,
    { gameId },
    { skip: permissionData && !permissionData.game?.canOnsenAccessVideo },
  );

  useEffect(() => {
    if (isPublicLeaguePage) {
      return;
    }
    if (currentUserData?.currentUser?.stubbed === true) {
      setStubbedModalOpen(true);
    }
  }, [currentUserData?.currentUser?.stubbed, isPublicLeaguePage]);

  const game = data?.game;

  useEffect(() => {
    const gaId = game?.org?.gaId;
    if (!gaId) {
      return;
    }
    gaSetSharedEventParams({ org_id: gaId });
  }, [game?.org?.gaId]);

  // A teamId in the URL that isn't one of this game's teams falls back to Home & Away
  const teamIdParamUnknown =
    !!teamIdParam && !!game && ![ALL_TEAMS, game.home?.id, game.away?.id].includes(teamIdParam);
  useEffect(() => {
    if (teamIdParamUnknown) teamIdSet(ALL_TEAMS);
  }, [teamIdParamUnknown]);

  // An explicit team choice in the URL overrides the user's default player
  const defaultSelection = !gamePlayerId && !teamIdParam ? dataDefaultGamePlayer?.game : null;
  const loadedGamePlayer = dataGamePlayer?.gamePlayer?.id === gamePlayerId ? dataGamePlayer.gamePlayer : null;
  const gamePlayer = gamePlayerId ? loadedGamePlayer : defaultSelection?.currentUserDefaultGamePlayer;
  const teamId =
    gamePlayer?.team?.id ||
    (teamIdParamUnknown ? ALL_TEAMS : teamIdParam) ||
    defaultSelection?.currentUserDefaultTeam?.id ||
    ALL_TEAMS;
  // Don't fall back to team sections while the player or the user's default is still on its way
  const selectionPending =
    (!!gamePlayerId && !loadedGamePlayer && loadingPlayer) ||
    (!gamePlayerId && !teamIdParam && !dataDefaultGamePlayer && loadingDefaultPlayer);
  const selectionKey = gamePlayer ? `player-${gamePlayer.id}` : `team-${teamId}`;
  const playerListButtonLabel =
    gamePlayer && gamePlayer.person
      ? `${gamePlayer.person.fullNameWithAnonymisation}${gamePlayer.playerNumber ? ` #${gamePlayer.playerNumber}` : ""}`
      : "All players";
  const handlePlayerSelect = (player) => {
    if (player?.id) {
      gamePlayerIdSet(player.id);
      teamIdSet(player.team.id);
    }
  };
  const handleTeamSelect = (newTeamId) => {
    gamePlayerIdSet(null);
    teamIdSet(newTeamId);
  };
  const isLoading = loadingUser || loadingPermission || loadingGame || loadingPlayer || loadingDefaultPlayer;

  const subNavRef = useRef(null);
  const boxScoreRef = useRef(null);
  const pointChartRef = useRef(null);
  const shotChartRef = useRef(null);
  const reelsRef = useRef(null);
  const highlightsRef = useRef(null);
  const [activeSection, activeSectionSet] = useState("reels");
  const scrollTo = (ref, key) => {
    activeSectionSet(key);
    if (!ref.current) return;
    ref.current.style.scrollMarginTop = `${subNavRef.current?.offsetHeight ?? 0}px`;
    ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (permissionData?.game && !permissionData.game.canOnsenAccessVideo) {
    return <UnauthorisedPage currentUser={currentUserData?.currentUser} />;
  }

  if (!game) {
    return <NotFoundPage />;
  }

  const hasVideo = ["available", "archived"].includes(game?.gameVideoStatus);
  const hasShotChartData = shotChartAvailableData?.game?.participations?.some((p) =>
    p.gamePlayers?.some((gp) => gp.eventsAny),
  );
  const navItems = [
    hasVideo && { key: "reels", label: "Reels", ref: reelsRef },
    hasVideo && { key: "highlights", label: "Highlights", ref: highlightsRef },
    { key: "boxscore", label: "Box Score", ref: boxScoreRef },
    hasShotChartData && { key: "shotchart", label: "Shot Chart", ref: shotChartRef },
    { key: "pointchart", label: "Point Chart", ref: pointChartRef },
  ].filter(Boolean);

  return (
    <>
      <PageTitle title="Game Overview" />
      {!isPublicLeaguePage && (
        <UpdateStubbedUserEmailModal open={stubbedModalOpen} onClose={() => setStubbedModalOpen(false)} />
      )}

      <GameOverviewPageHeader game={game} tab={tab} />
      {game?.statsVisible && (
        <>
          <div
            ref={subNavRef}
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "#ffffff",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{
                width: `calc(100vw - ${SPACING} * 2)`,
                margin: "0 auto",
                maxWidth: MAX_CONTENT_WIDTH,
                overflowX: "auto",
              }}
            >
              <div style={{ display: "flex", width: "fit-content" }}>
                {navItems.map(({ key, label, ref }) => {
                  const active = activeSection === key;
                  return (
                    <ButtonBase
                      key={key}
                      onClick={() => scrollTo(ref, key)}
                      style={{
                        padding: "14px 14px",
                        fontFamily: FONT_FAMILY_GRAPHIK,
                        fontSize: "0.85rem",
                        fontWeight: active ? 600 : 400,
                        color: active ? "#000000" : "#9e9e9e",
                        borderBottom: active ? "2px solid #000000" : "2px solid transparent",
                        backgroundColor: "transparent",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </ButtonBase>
                  );
                })}
              </div>
            </div>
          </div>
          <PageContentStack>
            <Stack style={{ width: `calc(100vw - ${SPACING} * 2)`, margin: "0 auto", maxWidth: MAX_CONTENT_WIDTH }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: SPACING_DENSE }}>
                <TeamListDropdown game={game} selectedTeamId={teamId} onSelect={handleTeamSelect} />
                <PlayerListDropdown
                  game={game}
                  teamId={teamId === ALL_TEAMS ? null : teamId}
                  selectedPlayerId={gamePlayer?.id}
                  onSelect={handlePlayerSelect}
                  onTeamSelect={handleTeamSelect}
                  buttonLabel={playerListButtonLabel}
                  isLoading={!Array.isArray(game?.gamePlayers)}
                />
              </div>
              <div ref={reelsRef}>
                {hasVideo && !selectionPending && (!tab || tab === "reels") && (
                  <GameOverviewReels key={selectionKey} game={game} gamePlayer={gamePlayer} teamId={teamId} />
                )}
              </div>
              <div ref={highlightsRef}>
                {hasVideo && !selectionPending && (!tab || tab === "highlights") && (
                  <GameOverviewHighlights key={selectionKey} gameId={gameId} gamePlayer={gamePlayer} teamId={teamId} />
                )}
              </div>
              <div ref={boxScoreRef}>
                {(!tab || tab === "boxscore") && <BoxScore game={game} gamePlayer={gamePlayer} />}
              </div>
              <div ref={shotChartRef}>{(!tab || tab === "shotchart") && <ShotChart gameId={gameId} />}</div>
              <div ref={pointChartRef}>{(!tab || tab === "pointchart") && <PointChart gameId={gameId} />}</div>
            </Stack>
          </PageContentStack>
        </>
      )}
    </>
  );
}
